import { Autocomplete, createFilterOptions } from '@mui/material';
import { TextField } from '@side/pantry';
import type { FocusEvent, ReactNode, Ref, SyntheticEvent } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { DropdownLoadingIcon } from '@/components/DropdownLoadingIcon';

export type MlsVendorOption = {
  id: string;
  /** Full MLS name, e.g. "California Regional MLS" */
  name: string;
  /** Short MLS code, e.g. "CRMLS" */
  code: string;
  /** Home Junction market code used for MLS search */
  providerId: string;
  listingIdTip: string | null;
  locales: string[];
};

const filter = createFilterOptions<MlsVendorOption>({
  stringify: (option) => `${option.name} ${option.code}`,
});

interface BaseProps {
  name?: string;
  label: ReactNode;
  ariaLabel?: string;
  options: MlsVendorOption[];
  /** Selected MLS id, or empty string for no selection. */
  value: string;
  onSelect: (option: MlsVendorOption | null, event: SyntheticEvent) => void;
  onBlur?: () => void;
  loading?: boolean;
  disabled?: boolean;
  loadingLabel?: string;
  error?: boolean;
  helperText?: string;
  fixedHeight?: boolean;
  /**
   * Saved selection that is not in `options` — an MLS outside the listing's
   * state, or a retired catalog entry. Rendered as the current value and
   * shown disabled in the list, so it keeps its label without being
   * re-pickable.
   */
  savedOption?: MlsVendorOption | null;
  inputRef?: Ref<HTMLInputElement>;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
}

/**
 * Controlled searchable MLS vendor picker: opens with the full list and
 * filters on MLS name or code as the user types. Mirrors the identity-ui
 * onboarding membership dropdowns.
 */
export const MlsVendorAutocompleteBase = ({
  name,
  label,
  ariaLabel,
  options,
  value,
  onSelect,
  onBlur,
  loading = false,
  disabled = false,
  loadingLabel,
  error = false,
  helperText,
  fixedHeight,
  savedOption,
  inputRef,
  onFocus,
}: BaseProps) => {
  const allOptions =
    savedOption && !options.some((option) => option.id === savedOption.id)
      ? [savedOption, ...options]
      : options;
  const selected = allOptions.find((option) => option.id === value) ?? null;

  return (
    <Autocomplete
      options={allOptions}
      getOptionDisabled={(option) => option.id === savedOption?.id}
      value={selected}
      onChange={(event, option) => onSelect(option, event)}
      onBlur={onBlur}
      loading={loading}
      disabled={loading || disabled}
      getOptionLabel={(option) => `${option.name} (${option.code})`}
      isOptionEqualToValue={(option, selectedOption) =>
        option.id === selectedOption.id
      }
      filterOptions={filter}
      noOptionsText="No MLS found"
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder="Select and search"
          ref={inputRef}
          error={error}
          helperText={helperText}
          fixedHeight={fixedHeight}
          onFocus={onFocus}
          fullWidth
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: loading ? (
                <DropdownLoadingIcon aria-label={loadingLabel} />
              ) : (
                params.slotProps.input.endAdornment
              ),
            },
            htmlInput: {
              ...params.slotProps.htmlInput,
              ...(ariaLabel && { 'aria-label': ariaLabel }),
            },
          }}
        />
      )}
    />
  );
};

interface Props {
  name: string;
  label: string;
  options: MlsVendorOption[];
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  fixedHeight?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
}

/**
 * React Hook Form–bound MLS vendor picker. Stores the selected MLS id in the
 * form field named by `name`.
 */
export const MlsVendorAutocomplete = ({
  name,
  label,
  options,
  loading = false,
  error = false,
  helperText,
  fixedHeight,
  onFocus,
}: Props) => {
  const { control } = useFormContext();
  const {
    field: { ref, value, onChange, onBlur },
  } = useController({ name, control });

  return (
    <MlsVendorAutocompleteBase
      name={name}
      label={label}
      options={options}
      value={typeof value === 'string' ? value : ''}
      onSelect={(option) => onChange(option?.id ?? '')}
      onBlur={onBlur}
      loading={loading}
      error={error}
      helperText={helperText}
      fixedHeight={fixedHeight}
      inputRef={ref}
      onFocus={onFocus}
    />
  );
};
